import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { type BlockerFunction, Link, useBlocker, useNavigate } from 'react-router-dom';
import { Button, Loading, Module, ModuleBody, ModuleFooter } from '@wfp/ui';

import { type Section, SectionAction, SectionState } from '../../restapi/types';
import { useDedobUser } from '../../hooks/home';
import { SectionForm } from './SectionForm';
import { SectionView } from './SectionView';
import {
	changeSectionState,
	saveSection,
	unlockSections,
	userCanChangeSection,
	userCanClearSection,
	userCanDeleteSection,
	userCanRejectSection,
	userCanSubmitSection,
	userCanUnlockSection,
	userCanWithdrawSection,
} from '../../restapi/section';
import confirm from '../common/dialogs/Confirm';


interface Props {
	docId?: string;
	section: Section;
}

const noChanges: Partial<Section> = {};
const pageLeaveConfirmationMsg = 'There are unsaved changes. Do you really want to leave this page lose the changes?';

export function SectionEdit({section: sec, docId}: Props) {
	const user = useDedobUser();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [isSaving, setIsSaving] = useState(false);
	const [isDirty, setIsDirty] = useState<boolean>(false);
	const [sectionChanges, setSectionChanges] = useState<Partial<Section>>(noChanges);
	const saveMutation = useMutation((changes: Partial<Section>) => saveSection(sec, changes), {
		onMutate: () => setIsSaving(true),
		onSuccess: () => {
			setSectionChanges(noChanges);
			queryClient.invalidateQueries('doc');
			queryClient.invalidateQueries('section');
		},
		onSettled: () => setIsSaving(false),
	});

	useEffect(() => {
		setIsDirty(sectionChanges !== noChanges);
	}, [sectionChanges]);

	useEffect(() => {
		window.onbeforeunload = !isDirty ? null :
			(e: BeforeUnloadEvent) => {
				e.preventDefault();
				e.returnValue = pageLeaveConfirmationMsg;
				return pageLeaveConfirmationMsg;
			};
	}, [isDirty]);

	const shouldBlock = React.useCallback<BlockerFunction>(
		({currentLocation, nextLocation}) =>
			(currentLocation.pathname !== nextLocation.pathname) && (isDirty),
		[isDirty],
	);
	const blocker = useBlocker(shouldBlock);

	useEffect(() => {
		if (blocker.state === 'blocked') {
			if (!isDirty) {
				blocker.proceed();
			} else {
				// just blocked with unsaved changes, ask confirmation
				confirm({
					title: 'Unsaved changes',
					message: pageLeaveConfirmationMsg,
					okLabel: 'Continue',
					cancelLabel: 'Cancel',
					type: 'danger',
				}).then(yes => {
					if (yes) {
						blocker.proceed();
					} else {
						blocker.reset();
					}
				});
			}
		}
	}, [blocker, isDirty]);

	const changeStateMutation = useMutation((action: SectionAction) => changeSectionState(sec, action), {
		onMutate: () => setIsSaving(true),
		onSuccess: (_, action) => {
			queryClient.invalidateQueries(['doc', docId]);
			queryClient.invalidateQueries('section');
			let nextSectionId = 0;
			if ((action === SectionAction.clear) || (action === SectionAction.reject)) {
				// FUTURE: go to next clearing section in this edition (if any)
			}
			navigate(nextSectionId ? `/section/${nextSectionId}` : `/${docId}`);
		},
		onSettled: () => setIsSaving(false),
	});

	const unlock = useCallback(
		() => unlockSections([sec]).then(() => {
			queryClient.invalidateQueries('doc');
			queryClient.invalidateQueries('section');
		}), [sec, queryClient],
	);

	const confirmDelete = useCallback(
		() => {
			confirm({
				title: 'Delete section',
				message: `Do you really want to delete this section?`,
				okLabel: 'Delete',
				cancelLabel: 'Cancel',
				type: 'danger',
			}).then(yes => {
				if (yes) {
					changeStateMutation.mutate(SectionAction.delete);
				}
			});
		}, [changeStateMutation],
	);

	if (!user) {
		return null;
	}

	const canEdit = userCanChangeSection(sec, user);
	const isDrafting = (sec.state === SectionState.new) || (sec.state === SectionState.drafted);
	const isLockedByOthers = sec.owner && (sec.owner !== user.username);
	const canDelete = !isLockedByOthers && userCanDeleteSection(sec, user);
	const canSubmit = !isLockedByOthers && userCanSubmitSection(sec, user);
	const canClear = !isLockedByOthers && userCanClearSection(sec, user);
	const canReject = !isLockedByOthers && userCanRejectSection(sec, user);
	const canWithdraw = !isLockedByOthers && userCanWithdrawSection(sec, user);
	const canUnlock = isLockedByOthers && userCanUnlockSection(sec, user);

	const disabled = isSaving;
	const hasChanges = (sectionChanges !== noChanges);
	const hasContent = (sectionChanges.content === undefined) ? !!sec.content : !!sectionChanges.content;

	return (
		<Module>
			<ModuleBody>
				{canEdit ?
					<SectionForm section={sec} disabled={disabled}
								 onChange={changes => setSectionChanges({...sectionChanges, ...changes})}
					/> :
					<SectionView section={sec}/>
				}
			</ModuleBody>
			<ModuleFooter className="justify-content-right">
				{canDelete ? <Button disabled={disabled} kind="danger"
									 onClick={() => confirmDelete()}
				>Delete</Button> : <div/>}
				<div className="col-fill"/>
				{isSaving && <div><Loading withOverlay={false} active small/></div>}
				<div className="col-fill"/>
				<Link to={`/${docId}/`}><Button disabled={disabled || !docId} kind="secondary">Cancel</Button></Link>
				{canEdit && <Button disabled={disabled || !hasChanges} kind="primary" className="ml"
									onClick={() => saveMutation.mutate(sectionChanges)}>{isDrafting ? 'Save as draft' : 'Save'}</Button>}
				{canSubmit && <Button disabled={disabled || hasChanges || !hasContent} kind="accent" className="ml"
									  onClick={() => changeStateMutation.mutate(SectionAction.submit)}>Submit to
					clearer</Button>}
				{canClear && <Button disabled={disabled || hasChanges || !hasContent} kind="accent" className="ml"
									 onClick={() => changeStateMutation.mutate(SectionAction.clear)}>Clear for
					publication</Button>}
				{(canReject || canWithdraw) && <Button disabled={disabled || hasChanges} kind="danger" className="ml"
													   onClick={() => changeStateMutation.mutate(SectionAction.reject)}>Send
					back to drafter</Button>}
				{canUnlock && <Button disabled={disabled} kind="danger" className="ml"
									  onClick={() => unlock()}>Force unlock from: {sec.owner}</Button>}
			</ModuleFooter>
		</Module>
	);
}